import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import BulkUploadButton from "@/components/BulkUploadButton";

const empty = { name: "", process: "", price_per_piece: 0, contact: "", notes: "" };

export default function Vendors() {
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = async () => { setList((await api.get("/vendors")).data); };
    useEffect(() => { load(); }, []);

    const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
    const startEdit = (v) => { setEditing(v); setForm({ ...v }); setOpen(true); };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name required"); return; }
        const payload = { ...form, price_per_piece: Number(form.price_per_piece) || 0 };
        try {
            if (editing) await api.put(`/vendors/${editing.vendor_id}`, payload);
            else await api.post("/vendors", payload);
            setOpen(false); await load();
            toast.success("Saved");
        } catch { toast.error("Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete vendor?")) return;
        await api.delete(`/vendors/${id}`); await load();
    };

    return (
        <div>
            <PageHeader
                eyebrow="Master" title="Outsource Vendors"
                subtitle="External processing vendors — heat treatment, plating, painting, EDM, etc."
                actions={
                    <div className="flex gap-2">
                        <BulkUploadButton entity="vendors" onComplete={load} />
                        <Button onClick={startCreate} className="rounded-none bg-[#264653] hover:bg-[#1e3742]" data-testid="new-vendor-button"><Plus size={16} className="mr-2" /> Add Vendor</Button>
                    </div>
                }
            />
            <div className="px-8 py-6">
                {list.length === 0 ? (
                    <div className="border-2 border-dashed border-zinc-300 p-16 text-center">
                        <div className="tag-pill mb-4 inline-flex">Empty</div>
                        <h2 className="text-2xl font-bold tracking-tight">No vendors yet</h2>
                        <Button onClick={startCreate} className="mt-6 rounded-none bg-[#264653] hover:bg-[#1e3742]"><Plus size={16} className="mr-2" /> Add now</Button>
                    </div>
                ) : (
                    <table className="w-full sheet-table" data-testid="vendors-table">
                        <thead><tr><th>Vendor</th><th>Process</th><th>Price / piece (₹)</th><th>Contact</th><th>Notes</th><th></th></tr></thead>
                        <tbody>
                            {list.map((v) => (
                                <tr key={v.vendor_id} data-testid={`vendor-row-${v.vendor_id}`}>
                                    <td className="font-medium">{v.name}</td>
                                    <td>{v.process || "—"}</td>
                                    <td className="font-mono">{v.price_per_piece}</td>
                                    <td className="font-mono text-xs">{v.contact}</td>
                                    <td className="text-zinc-500 text-xs">{v.notes}</td>
                                    <td className="text-right w-32">
                                        <button onClick={() => startEdit(v)} className="text-[#002FA7] hover:underline text-xs uppercase tracking-wider">Edit</button>
                                        <button onClick={() => remove(v.vendor_id)} className="ml-3 text-zinc-400 hover:text-[#E63946]"><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-none max-w-lg">
                    <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Vendor</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Name *"><Input data-testid="vendor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        <Field label="Process"><Input data-testid="vendor-process-input" value={form.process} onChange={(e) => setForm({ ...form, process: e.target.value })} placeholder="e.g. Heat treatment" /></Field>
                        <Field label="Price / piece (₹)"><Input type="number" data-testid="vendor-price-input" value={form.price_per_piece} onChange={(e) => setForm({ ...form, price_per_piece: e.target.value })} /></Field>
                        <Field label="Contact"><Input data-testid="vendor-contact-input" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Phone / email" /></Field>
                        <div className="col-span-2">
                            <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button className="rounded-none bg-[#264653] hover:bg-[#1e3742]" onClick={save} data-testid="save-vendor-button">{editing ? "Update" : "Create"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, children }) {
    return <div className="space-y-1.5"><Label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>{children}</div>;
}
